import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Markdown, { type Components } from 'react-markdown';
import NotFound from '../NotFound';
import { LegalPage, P, Ul, Ol, B, useLegalDocumentHead } from '../legal/components';
import { CATEGORY_LABEL, fetchNotice, formatNoticeDate, NoticeNotFoundError, type Notice } from './noticeApi';

const linkClass = 'text-red-400 hover:text-red-300';

/** Markdown → 약관 페이지와 같은 타이포그래피. 내부 경로(/...)는 SPA Link, 외부는 새 탭. raw HTML은 렌더하지 않는다(react-markdown 기본). */
const components: Components = {
  p: ({ children }) => <P>{children}</P>,
  ul: ({ children }) => <Ul>{children}</Ul>,
  ol: ({ children }) => <Ol>{children}</Ol>,
  strong: ({ children }) => <B>{children}</B>,
  a: ({ href, children }) =>
    href?.startsWith('/') ? (
      <Link to={href} className={linkClass}>{children}</Link>
    ) : (
      <a href={href} className={linkClass} target="_blank" rel="noopener noreferrer">{children}</a>
    ),
};

type State = { kind: 'loading' } | { kind: 'notFound' } | { kind: 'error' } | { kind: 'ready'; notice: Notice };

export default function NoticeDetail() {
  const { id } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  useLegalDocumentHead(state.kind === 'ready' ? `${state.notice.title} | Cupid` : '공지사항 | Cupid', false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchNotice(id)
      .then((notice) => { if (!cancelled) setState({ kind: 'ready', notice }); })
      .catch((e) => { if (!cancelled) setState({ kind: e instanceof NoticeNotFoundError ? 'notFound' : 'error' }); });
    return () => { cancelled = true; };
  }, [id]);

  if (state.kind === 'notFound') return <NotFound />;

  const backLink = (
    <div className="mt-10 border-t border-gray-800 pt-6">
      <Link to="/notices" className="text-sm text-gray-400 transition-colors hover:text-white">← 공지사항 목록</Link>
    </div>
  );

  if (state.kind !== 'ready') {
    return (
      <LegalPage title="공지사항">
        <p className="py-16 text-center text-gray-500">
          {state.kind === 'loading' ? '불러오는 중…' : '공지사항을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'}
        </p>
        {backLink}
      </LegalPage>
    );
  }

  const { notice } = state;
  return (
    <LegalPage
      title={notice.title}
      subtitle={<>{CATEGORY_LABEL[notice.category]} · {formatNoticeDate(notice.publishedAt)}</>}
    >
      <Markdown components={components}>{notice.body}</Markdown>
      {backLink}
    </LegalPage>
  );
}
